import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, RotateCcw, History, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Each section declares its editable fields. Type drives the input widget.
const SECTION_DEFS = [
  { id: "announcement", label: "Announcement bar", fields: [
    { key: "enabled", type: "boolean", label: "Visible on site" },
    { key: "text", type: "text", label: "Announcement text" },
    { key: "link_label", type: "text", label: "Link label (optional)" },
    { key: "link_url", type: "text", label: "Link URL (optional)" },
  ]},
  { id: "home_hero", label: "Home — Hero", fields: [
    { key: "eyebrow", type: "text", label: "Eyebrow line" },
    { key: "headline_line1", type: "text", label: "Headline (line 1)" },
    { key: "headline_line2", type: "text", label: "Headline (line 2, italic green)" },
    { key: "subhead", type: "longtext", label: "Subhead paragraph" },
    { key: "badge_text", type: "text", label: "Badge text" },
    { key: "primary_cta_label", type: "text", label: "Primary button label" },
    { key: "primary_cta_url", type: "text", label: "Primary button URL" },
    { key: "secondary_cta_label", type: "text", label: "Secondary button label" },
    { key: "secondary_cta_url", type: "text", label: "Secondary button URL" },
    { key: "script_tag", type: "text", label: "Script-style overlay text" },
    { key: "scroll_cue", type: "text", label: "Scroll cue text" },
  ]},
  { id: "home_about", label: "Home — About Heather", fields: [
    { key: "eyebrow", type: "text", label: "Eyebrow" },
    { key: "heading", type: "text", label: "Heading" },
    { key: "body", type: "longtext", label: "Body (use \\n\\n to split paragraphs)" },
    { key: "stat1_value", type: "text", label: "Stat 1 — value" },
    { key: "stat1_label", type: "text", label: "Stat 1 — label" },
    { key: "stat2_value", type: "text", label: "Stat 2 — value" },
    { key: "stat2_label", type: "text", label: "Stat 2 — label" },
    { key: "stat3_value", type: "text", label: "Stat 3 — value" },
    { key: "stat3_label", type: "text", label: "Stat 3 — label" },
  ]},
  { id: "home_learn", label: "Home — What you'll learn", fields: [
    { key: "eyebrow", type: "text", label: "Eyebrow" },
    { key: "heading", type: "text", label: "Heading" },
    { key: "intro", type: "longtext", label: "Intro paragraph" },
    { key: "bullets", type: "list", label: "Bullet points" },
    { key: "age_note", type: "longtext", label: "Age policy note" },
  ]},
  { id: "home_classes", label: "Home — Upcoming classes", fields: [
    { key: "eyebrow", type: "text", label: "Eyebrow" },
    { key: "heading", type: "text", label: "Heading" },
    { key: "intro", type: "longtext", label: "Intro paragraph" },
    { key: "empty_state_heading", type: "text", label: "Empty state heading (when no classes)" },
    { key: "empty_state_body", type: "longtext", label: "Empty state body" },
    { key: "empty_state_cta_label", type: "text", label: "Empty state CTA label" },
    { key: "empty_state_cta_url", type: "text", label: "Empty state CTA URL" },
  ]},
  { id: "home_1on1", label: "Home — 1-on-1 CTA", fields: [
    { key: "eyebrow", type: "text", label: "Eyebrow" },
    { key: "heading", type: "text", label: "Heading" },
    { key: "body", type: "longtext", label: "Body" },
    { key: "cta_label", type: "text", label: "CTA button label" },
    { key: "cta_url", type: "text", label: "CTA button URL" },
  ]},
  { id: "home_faq", label: "Home — FAQ", fields: [
    { key: "eyebrow", type: "text", label: "Eyebrow" },
    { key: "heading", type: "text", label: "Heading" },
    { key: "items", type: "faq", label: "FAQ items" },
  ]},
  { id: "services_hero", label: "Services page — Hero", fields: [
    { key: "eyebrow", type: "text", label: "Eyebrow" },
    { key: "heading", type: "text", label: "Heading" },
    { key: "body", type: "longtext", label: "Body" },
  ]},
  { id: "consultation_hero", label: "Consultation page — Hero", fields: [
    { key: "eyebrow", type: "text", label: "Eyebrow" },
    { key: "heading", type: "text", label: "Heading" },
    { key: "body", type: "longtext", label: "Body" },
  ]},
];

export default function SiteCMSTab() {
  const { data: sections = [], refetch } = useQuery({
    queryKey: ["adminSiteContent"],
    queryFn: async () => (await api.get("/admin/site-content")).data,
  });
  const [activeId, setActiveId] = useState(SECTION_DEFS[0].id);

  const active = sections.find((s) => s.section_id === activeId);
  const def = SECTION_DEFS.find((s) => s.id === activeId);
  if (!def) return null;

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-6">
      <aside className="rounded-2xl border border-gg-border bg-gg-surface p-2 h-fit">
        {SECTION_DEFS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveId(s.id)}
            data-testid={`cms-section-${s.id}`}
            className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${
              activeId === s.id ? "bg-gg-green text-white font-semibold" : "text-[var(--gg-text)] hover:bg-gg-bg"
            }`}
          >
            {s.label}
          </button>
        ))}
      </aside>
      <main>
        {active && <SectionEditor def={def} section={active} onSaved={refetch} />}
      </main>
    </div>
  );
}

function SectionEditor({ def, section, onSaved }) {
  const [form, setForm] = useState(section.fields || {});
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { setForm(section.fields || {}); }, [section.section_id, section.fields]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/site-content/${def.id}`, { fields: form });
      toast.success("Saved — refresh the live site to see changes.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const resetToDefaults = () => {
    if (!confirm("Replace current fields with built-in defaults? You can still restore from history after saving.")) return;
    setForm(section.defaults || {});
  };

  const restoreVersion = async (idx) => {
    if (!confirm(`Restore version saved on ${section.versions[idx]?.saved_at || "—"}? Your current edit will be saved as a backup.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/site-content/${def.id}/restore/${idx}`, {});
      setForm(data.fields || {});
      toast.success("Version restored");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-gg-border bg-gg-surface p-5 sm:p-7">
      <div className="flex items-center justify-between mb-5 gap-2 flex-wrap">
        <div>
          <h3 className="font-heading text-2xl text-[var(--gg-text)]">{def.label}</h3>
          <p className="text-xs text-gg-muted mt-1">
            Edits go live after saving. Visitors may need to refresh the site to see changes.
          </p>
        </div>
        <div className="flex gap-2">
          {section.versions?.length > 0 && (
            <button type="button" onClick={() => setShowHistory((s) => !s)} className="text-xs px-3 py-1.5 rounded-full border border-gg-border text-gg-brown hover:border-gg-green inline-flex items-center gap-1">
              <History size={12} /> History ({section.versions.length})
            </button>
          )}
          <button type="button" onClick={resetToDefaults} className="text-xs px-3 py-1.5 rounded-full border border-gg-border text-gg-brown hover:border-gg-green inline-flex items-center gap-1">
            <RotateCcw size={12} /> Reset
          </button>
          <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary text-xs disabled:opacity-60" data-testid="cms-save-btn">
            <Save size={14} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {showHistory && (
        <div className="rounded-xl border border-gg-border bg-gg-bg p-3 mb-5 space-y-1">
          {section.versions.map((v, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-gg-muted">{v.saved_at || "—"}</span>
              <button type="button" onClick={() => restoreVersion(i)} className="text-gg-green hover:underline">Restore this version</button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {def.fields.map((f) => (
          <FieldEditor key={f.key} field={f} value={form[f.key]} onChange={(v) => set(f.key, v)} />
        ))}
      </div>
    </div>
  );
}

function FieldEditor({ field, value, onChange }) {
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" data-testid={`cms-field-${field.key}`} />
        <span className="text-sm font-semibold text-[var(--gg-text)]">{field.label}</span>
      </label>
    );
  }
  if (field.type === "longtext") {
    return (
      <label className="block">
        <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">{field.label}</span>
        <Textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" data-testid={`cms-field-${field.key}`} />
      </label>
    );
  }
  if (field.type === "list") {
    const items = Array.isArray(value) ? value : [];
    return (
      <div>
        <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-2">{field.label}</p>
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={item} onChange={(e) => { const next = [...items]; next[i] = e.target.value; onChange(next); }} className="flex-1" data-testid={`cms-field-${field.key}-${i}`} />
              <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-gg-muted hover:text-[#B24C4C] p-1.5"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => onChange([...items, ""])} className="text-xs mt-2 px-3 py-1 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white inline-flex items-center gap-1" data-testid={`cms-field-${field.key}-add`}>
          <Plus size={12} /> Add item
        </button>
      </div>
    );
  }
  if (field.type === "faq") {
    const items = Array.isArray(value) ? value : [];
    return (
      <div>
        <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-2">{field.label}</p>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="rounded-lg border border-gg-border bg-gg-bg p-3 space-y-2">
              <Input value={it.q || ""} onChange={(e) => { const next = [...items]; next[i] = { ...it, q: e.target.value }; onChange(next); }} placeholder="Question" data-testid={`cms-faq-q-${i}`} />
              <Textarea value={it.a || ""} onChange={(e) => { const next = [...items]; next[i] = { ...it, a: e.target.value }; onChange(next); }} rows={3} placeholder="Answer" data-testid={`cms-faq-a-${i}`} />
              <div className="flex justify-end">
                <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-xs text-[#B24C4C] hover:underline inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => onChange([...items, { q: "", a: "" }])} className="text-xs mt-2 px-3 py-1 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white inline-flex items-center gap-1" data-testid={`cms-faq-add`}>
          <Plus size={12} /> Add FAQ
        </button>
      </div>
    );
  }
  // default: text
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">{field.label}</span>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" data-testid={`cms-field-${field.key}`} />
    </label>
  );
}
